import { useState, useMemo, useCallback } from "react";
import {
  Card,
  Space,
  Select,
  DatePicker,
  Button,
  Switch,
  InputNumber,
  Row,
  Col,
} from "antd";
import {
  ReloadOutlined,
  SearchOutlined,
  ShopOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { useAppDispatch } from "../../../app/hooks";
import { getBusinessesThunk } from "../../businesses/businessesSlice";
import { IBusinessFull } from "../../../interfaces/businessFull.interface";
import debounce from "lodash/debounce";

const { RangePicker } = DatePicker;

// Hospitality MCC codes
const HOSPITALITY_MCC_CODES = [5811, 5812, 5813, 5814];

interface HospitalityFiltersProps {
  selectedBusinessId: string | null;
  onBusinessChange: (businessId: string | null) => void;
  dateRange: [Dayjs | null, Dayjs | null];
  onDateRangeChange: (range: [Dayjs | null, Dayjs | null]) => void;
  segment: string;
  onSegmentChange: (segment: string) => void;
  includeCustomerList: boolean;
  onIncludeCustomerListChange: (include: boolean) => void;
  limit: number;
  onLimitChange: (limit: number) => void;
  onRefresh: () => void;
  loading: boolean;
  isMobile: boolean;
}

export default function HospitalityFilters({
  selectedBusinessId,
  onBusinessChange,
  dateRange,
  onDateRangeChange,
  segment,
  onSegmentChange,
  includeCustomerList,
  onIncludeCustomerListChange,
  limit,
  onLimitChange,
  onRefresh,
  loading,
  isMobile,
}: HospitalityFiltersProps) {
  const dispatch = useAppDispatch();
  const [businessOptions, setBusinessOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [businessSearching, setBusinessSearching] = useState(false);

  // Data collection started on Dec 5, 2025
  const DATA_COLLECTION_START_DATE = dayjs("2025-12-05");
  const today = dayjs();

  const disabledDate = (current: Dayjs | null) => {
    if (!current) return false;
    return (
      current.isBefore(DATA_COLLECTION_START_DATE, "day") ||
      current.isAfter(today, "day")
    );
  };

  const searchBusinesses = useCallback(async (q?: string) => {
    setBusinessSearching(true);
    try {
      const { payload }: any = await dispatch(
        getBusinessesThunk({
          currentPage: 1,
          pageSize: 20,
          name: q?.toLowerCase(),
        } as any)
      );
      // Filter by hospitality MCC codes
      const hospitalityBusinesses = (payload?.data || []).filter(
        (b: IBusinessFull) =>
          b.merchantCategoryCode &&
          HOSPITALITY_MCC_CODES.includes(b.merchantCategoryCode)
      );
      const opts = hospitalityBusinesses.map((b: IBusinessFull) => ({
        value: b._id,
        label: `${b.businessName} (${b.rootUser?.email || "-"})`,
      }));
      setBusinessOptions(opts);
    } catch (error) {
      console.error("Error searching businesses:", error);
    } finally {
      setBusinessSearching(false);
    }
  }, [dispatch]);

  const debouncedSearch = useMemo(
    () => debounce((v: string) => searchBusinesses(v), 300),
    [searchBusinesses]
  );

  const handleBusinessSearch = (value: string) => {
    if (value) {
      debouncedSearch(value);
    } else {
      searchBusinesses();
    }
  };

  const handleDropdownOpen = (open: boolean) => {
    if (open && businessOptions.length === 0) {
      searchBusinesses();
    }
  };

  return (
    <Card className="filters_card">
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={8} lg={6}>
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <span style={{ fontSize: "12px", color: "rgba(0,0,0,0.45)" }}>
              Merchant <ShopOutlined />
            </span>
            <Select
              showSearch
              allowClear
              placeholder="Search hospitality merchant"
              value={selectedBusinessId}
              onChange={(value) => onBusinessChange(value)}
              onSearch={handleBusinessSearch}
              onDropdownVisibleChange={handleDropdownOpen}
              filterOption={false}
              notFoundContent={businessSearching ? "Searching..." : "No merchants found"}
              style={{ width: "100%" }}
              suffixIcon={<SearchOutlined />}
              options={businessOptions}
            />
          </Space>
        </Col>

        <Col xs={24} sm={24} md={8} lg={6}>
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <span style={{ fontSize: "12px", color: "rgba(0,0,0,0.45)" }}>
              Date Range <CalendarOutlined />
            </span>
            <RangePicker
              value={dateRange}
              onChange={(dates) =>
                onDateRangeChange([
                  dates?.[0] || null,
                  dates?.[1] || null,
                ])
              }
              disabledDate={disabledDate}
              format="YYYY-MM-DD"
              style={{ width: "100%" }}
              placeholder={["Start date", "End date"]}
            />
          </Space>
        </Col>

        <Col xs={24} sm={12} md={4} lg={3}>
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <span style={{ fontSize: "12px", color: "rgba(0,0,0,0.45)" }}>
              Segment
            </span>
            <Select
              value={segment}
              onChange={onSegmentChange}
              style={{ width: "100%" }}
              options={[
                { value: "all", label: "All" },
                { value: "vip", label: "VIP" },
                { value: "regular", label: "Regular" },
                { value: "occasional", label: "Occasional" },
                { value: "one-time", label: "One-time" },
              ]}
            />
          </Space>
        </Col>

        <Col xs={12} sm={6} md={2} lg={2}>
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <span style={{ fontSize: "12px", color: "rgba(0,0,0,0.45)" }}>
              Limit
            </span>
            <InputNumber
              value={limit}
              onChange={(value) => onLimitChange(value || 100)}
              min={1}
              max={1000}
              style={{ width: "100%" }}
            />
          </Space>
        </Col>

        <Col xs={12} sm={6} md={2} lg={2}>
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <span style={{ fontSize: "12px", color: "rgba(0,0,0,0.45)" }}>
              Customer List
            </span>
            <Switch
              checked={includeCustomerList}
              onChange={onIncludeCustomerListChange}
              style={{ width: "100%" }}
            />
          </Space>
        </Col>

        <Col xs={24} sm={24} md={24} lg={5}>
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <span style={{ fontSize: "12px", color: "rgba(0,0,0,0.45)" }}>
              Actions
            </span>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={onRefresh}
              loading={loading}
              disabled={!selectedBusinessId}
              style={{ width: "100%" }}
            >
              {isMobile ? "Refresh" : "Refresh Data"}
            </Button>
          </Space>
        </Col>
      </Row>
    </Card>
  );
}

